// src/pages/PromotionPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import promotions from '../data/promotions';
import '../styles/PromotionPage.css';

/** Returns true if today falls within the promotion's active window */
const isVisible = (promo) => {
  if (!promo.active) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (promo.startDate && today < new Date(promo.startDate)) return false;
  if (promo.endDate) {
    const end = new Date(promo.endDate);
    end.setHours(23, 59, 59, 999);
    if (today > end) return false;
  }
  return true;
};

/** All images for a promo as an array */
const getImages = (promo) => {
  if (promo.images && promo.images.length) return promo.images;
  if (promo.image) return [promo.image];
  return [];
};

/* ─── Modal ──────────────────────────────────────────────── */
const PromoModal = ({ promo, onClose }) => {
  const images = getImages(promo);
  const hasMultiple = images.length > 1;

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div
      className="promo-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={promo.title}
    >
      <div
        className="promo-modal"
        onClick={(e) => e.stopPropagation()} // don't close when clicking inside
      >
        {/* Close button row — sticky at top */}
        <div className="promo-modal__close">
          <button
            className="promo-modal__close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Images */}
        {hasMultiple ? (
          <div className="promo-modal__img-strip-wrap">
            <div className="promo-modal__img-strip">
              {images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`${promo.title} ${i + 1}`}
                  loading="lazy"
                />
              ))}
            </div>
            <div className="promo-modal__scroll-hint" aria-hidden="true">
              <span>›</span>
            </div>
          </div>
        ) : images.length === 1 ? (
          <img
            src={images[0]}
            alt={promo.title}
            className="promo-modal__img"
            loading="lazy"
          />
        ) : null}

        {/* Body */}
        <div className="promo-modal__body">
          <span className={`promo-modal__tag promo-modal__tag--${promo.tagColor || 'gold'}`}>
            {promo.tag}
          </span>
          <h2 className="promo-modal__title">{promo.title}</h2>
          <hr className="promo-modal__divider" />
          <p className="promo-modal__content">{promo.content}</p>

          {promo.ctaHref && (
            <a
              href={promo.ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-gold promo-modal__cta"
            >
              {promo.ctaLabel || 'Learn More'}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Card ───────────────────────────────────────────────── */
const PromoCard = ({ promo, onClick }) => {
  const coverImage = promo.image || (promo.images && promo.images[0]);

  return (
    <article
      className="promo-card"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      aria-label={`View details for ${promo.title}`}
    >
      {/* Hover hint overlay */}
      <div className="promo-card__hover-hint" aria-hidden="true">
        <span>View Details</span>
      </div>

      {/* Cover image */}
      <div className="promo-card__img-wrap">
        <img
          src={coverImage}
          alt={promo.title}
          className="promo-card__img"
          loading="lazy"
        />
        <span className={`promo-card__tag promo-card__tag--${promo.tagColor || 'gold'}`}>
          {promo.tag}
        </span>
      </div>

      {/* Body */}
      <div className="promo-card__body">
        <h3 className="promo-card__title">{promo.title}</h3>
        <hr className="promo-card__divider" />
        <p className="promo-card__content">{promo.content}</p>
      </div>
    </article>
  );
};

/* ─── Page ───────────────────────────────────────────────── */
const PromotionPage = () => {
  const visible = promotions.filter(isVisible);
  const [selected, setSelected] = useState(null);

  const closeModal = useCallback(() => setSelected(null), []);

  return (
    <div className="promo-page">
      {/* Hero header */}
      <header className="promo-hero">
        <span className="promo-hero__eyebrow">What's On</span>
        <h1 className="promo-hero__title">Promotions & Events</h1>
        <p className="promo-hero__sub">
          From weekly cocktail nights to curated wine dinners — there's always
          something happening at Lebua Thai. Tap any card to learn more.
        </p>
      </header>

      {/* Promotion cards */}
      {visible.length > 0 ? (
        <div className="promo-grid">
          {visible.map((promo) => (
            <PromoCard
              key={promo.id}
              promo={promo}
              onClick={() => setSelected(promo)}
            />
          ))}
        </div>
      ) : (
        <p style={{ textAlign: 'center', padding: '4rem', color: 'var(--color-text-muted)' }}>
          No active promotions at the moment. Check back soon!
        </p>
      )}

      {/* Reserve CTA */}
      <section className="promo-reserve">
        <h2>Ready to Join Us?</h2>
        <p>Reserve a table or enquire about a corporate event.</p>
        <div className="promo-reserve__actions">
          <Link to="/reservation" className="btn btn-primary">Reserve a Table</Link>
          <a
            href="https://wa.me/6590079953?text=Hi%20Lebua%20Thai%2C%20I%27d%20like%20to%20book%20a%20corporate%20event."
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline"
          >
            Corporate Enquiry
          </a>
        </div>
      </section>

      {/* Social links */}
      <div className="promo-social">
        <p className="promo-social__title">Follow Us</p>
        <div className="promo-social__icons">
          <a href="https://www.facebook.com/profile.php?id=61556215584916" className="promo-social__icon" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
            <FontAwesomeIcon icon={faFacebookF} />
          </a>
          <a href="https://www.instagram.com/lebua_thai/" className="promo-social__icon" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <FontAwesomeIcon icon={faInstagram} />
          </a>
          <a href="https://g.co/kgs/Ct72hTQ" className="promo-social__icon" target="_blank" rel="noopener noreferrer" aria-label="Google">
            <FontAwesomeIcon icon={faGoogle} />
          </a>
        </div>
      </div>

      <Footer />

      {/* Modal — rendered outside normal flow via state */}
      {selected && (
        <PromoModal promo={selected} onClose={closeModal} />
      )}
    </div>
  );
};

export default PromotionPage;
